import { useEffect } from 'react';

/**
 * Advanced Font Optimizer for maximum performance
 * Implements aggressive font loading strategies to minimize CLS and improve LCP
 */
export function FontOptimizer() {
  useEffect(() => {
    // Preload critical system fonts and Google Fonts
    const criticalFonts = [
      'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiA.woff2',
      'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuGKYAZ9hiA.woff2'
    ];

    // Preload fonts with high priority
    criticalFonts.forEach(fontUrl => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'font';
      link.type = 'font/woff2';
      link.href = fontUrl;
      link.crossOrigin = 'anonymous';
      link.setAttribute('fetchpriority', 'high');
      document.head.appendChild(link);
    });

    // Preconnect to Google Fonts for faster DNS resolution
    const preconnectLinks = [
      'https://fonts.googleapis.com',
      'https://fonts.gstatic.com'
    ];

    preconnectLinks.forEach(url => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = url;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });

    // Add optimized font CSS with font-display: swap
    const style = document.createElement('style');
    style.textContent = `
      /* Aggressive font optimization for performance */
      @font-face {
        font-family: 'Inter';
        font-style: normal;
        font-weight: 400;
        font-display: swap;
        src: url('https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiA.woff2') format('woff2');
        unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
      }
      
      @font-face {
        font-family: 'Inter';
        font-style: normal;
        font-weight: 600;
        font-display: swap;
        src: url('https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuGKYAZ9hiA.woff2') format('woff2');
        unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
      }

      /* Fallback font stack for immediate rendering */
      body, html {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
        font-feature-settings: 'cv02', 'cv03', 'cv04', 'cv11';
        font-variation-settings: normal;
      }

      /* Optimize text rendering */
      * {
        text-rendering: optimizeSpeed;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }

      /* Prevent layout shift during font load */
      .font-loading {
        visibility: hidden;
      }
      
      .font-loaded {
        visibility: visible;
      }
    `;
    
    document.head.appendChild(style);

    // Font loading detection for better UX
    if ('fonts' in document) {
      Promise.all([
        document.fonts.load('400 16px Inter'),
        document.fonts.load('600 16px Inter')
      ]).then(() => {
        document.documentElement.classList.add('font-loaded');
        document.documentElement.classList.remove('font-loading');
      }).catch(() => {
        // Fallback if font loading fails
        document.documentElement.classList.add('font-loaded');
        document.documentElement.classList.remove('font-loading');
      });
    }

    // Cleanup function
    return () => {
      // Remove preload links and styles if component unmounts
      const preloadLinks = document.querySelectorAll('link[rel="preload"][as="font"]');
      preloadLinks.forEach(link => link.remove());
    };
  }, []);

  return null; // This component doesn't render anything
}

export default FontOptimizer;